import { Helmet } from 'react-helmet-async';
import React from 'react';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  ogUrl?: string;
  ogLocale?: string;
  ogSiteName?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  hreflang?: {
    'ar-eg'?: string;
    'en-us'?: string;
    'x-default'?: string;
  };
  jsonLd?: object;
  children?: React.ReactNode;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogImage,
  ogUrl,
  ogLocale,
  ogSiteName = 'Bouqueh',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  keywords,
  author,
  publishedTime,
  modifiedTime,
  section,
  tags,
  hreflang,
  jsonLd,
  children
}) => {
  // Always emit canonical when provided; react-helmet-async de-duplicates by tag attributes,
  // so any worker-injected canonical for the same href is replaced cleanly.
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Robots Meta */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Image Optimization Style */}
      <style>{`img:is([sizes="auto" i], [sizes^="auto," i]) { contain-intrinsic-size: 3000px 1500px }`}</style>
      
      {/* Critical CSS for layout stability */}
      <style>{`
        /* Prevent layout shift for images */
        img[src*="premium-flower-landingpage-desktop"],
        img[src*="luxury-flower-2"] {
          aspect-ratio: 380/102;
          object-fit: cover;
          width: 100%;
          height: auto;
        }
        
        /* Ensure proper font loading */
        .font-loading {
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .font-loading-ar {
          font-family: 'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        /* Prevent FOUC (Flash of Unstyled Content) */
        .fouc-fix {
          visibility: hidden;
        }
        
        .fouc-fix.loaded {
          visibility: visible;
        }
      `}</style>
      
      {/* Hreflang Links */}
      {hreflang && (
        <>
          {hreflang['ar-eg'] && <link rel="alternate" hrefLang="ar-eg" href={hreflang['ar-eg']} />}
          {hreflang['en-us'] && <link rel="alternate" hrefLang="en-us" href={hreflang['en-us']} />}
          {hreflang['x-default'] && <link rel="alternate" hrefLang="x-default" href={hreflang['x-default']} />}
        </>
      )}
      
      {/* Open Graph */}
      <meta property="og:locale" content={ogLocale || 'en_US'} />
      {ogLocale === 'ar_EG' && <meta property="og:locale:alternate" content="en_US" />}
      {ogLocale === 'en_US' && <meta property="og:locale:alternate" content="ar_EG" />}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      <meta property="og:site_name" content={ogSiteName} />
      {/*
        og:image. width/height/type intentionally only emitted when ogImage
        matches the bundled /og-image.webp (672×672 square). For pages that
        pass their own ogImage (product photos, holiday banners), we skip
        the explicit dimensions so we don't mislabel them — most crawlers
        sniff width/height anyway.
      */}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && (ogImage.endsWith('/og-image.webp') || /\/og-image\.webp$/.test(ogImage)) && (
        <>
          <meta property="og:image:width" content="672" />
          <meta property="og:image:height" content="672" />
          <meta property="og:image:type" content="image/webp" />
        </>
      )}
      
      {/* Article-specific meta tags */}
      {ogType === 'article' && (
        <>
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {author && <meta name="author" content={author} />}
          {section && <meta property="article:section" content={section} />}
          {tags && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle || title} />
      <meta name="twitter:description" content={twitterDescription || description} />
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}
      
      {/* Keywords */}
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json" className="yoast-schema-graph">
          {JSON.stringify(jsonLd)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

export default SEO; 